import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, FileText } from 'lucide-react'
import { getHomes, getItemsByHome, getRecurringExpensesByHome, daysUntil } from '../lib/supabase'
import { StatusBadge, TypeBadge, UrgencyBadge, Loading, EmptyState, formatCurrency } from '../components/ui'
import { STATUS_CONFIG, ANNUAL_MULTIPLIER } from '../lib/constants'

export default function StatusBoardPage() {
  const [homes, setHomes] = useState([])
  const [selectedHome, setSelectedHome] = useState(null)
  const [items, setItems] = useState([])
  const [expenses, setExpenses] = useState([])
  const [loading, setLoading] = useState(true)
  const [dataLoading, setDataLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('items')

  useEffect(() => {
    getHomes().then(h => {
      setHomes(h)
      if (h.length > 0) setSelectedHome(h[0].id)
      setLoading(false)
    })
  }, [])

  useEffect(() => {
    if (!selectedHome) return
    setDataLoading(true)
    Promise.all([
      getItemsByHome(selectedHome),
      getRecurringExpensesByHome(selectedHome)
    ]).then(([it, ex]) => {
      setItems(it); setExpenses(ex)
    }).finally(() => setDataLoading(false))
  }, [selectedHome])

  if (loading) return <div className="page-body"><Loading /></div>

  const STATUSES = ['Considering','Shortlisted','Purchased','Installed','Rejected']
  const committed = items.filter(i => i.status === 'Purchased' || i.status === 'Installed').reduce((s, i) => s + (i.cost || 0), 0)
  const totalEst = items.reduce((s, i) => s + (i.cost || 0), 0)
  const totalAnnualExp = expenses.reduce((s, e) => s + (e.cost || 0) * (ANNUAL_MULTIPLIER[e.period] || 1), 0)
  const overdueSoon = expenses.filter(e => { const d = daysUntil(e.next_due); return d !== null && d <= 14 })

  // Sort expenses: overdue first, then by date
  const sortedExpenses = [...expenses].sort((a, b) => {
    const da = daysUntil(a.next_due) ?? 9999
    const db = daysUntil(b.next_due) ?? 9999
    return da - db
  })

  const expGroups = [
    { label: 'Overdue', dot: '#B91C1C', items: sortedExpenses.filter(e => { const d = daysUntil(e.next_due); return d !== null && d < 0 }) },
    { label: 'Due within 2 weeks', dot: '#B45309', items: sortedExpenses.filter(e => { const d = daysUntil(e.next_due); return d !== null && d >= 0 && d <= 14 }) },
    { label: 'Next 90 days', dot: '#15803D', items: sortedExpenses.filter(e => { const d = daysUntil(e.next_due); return d !== null && d > 14 && d <= 90 }) },
    { label: 'Later', dot: '#9E9E9E', items: sortedExpenses.filter(e => { const d = daysUntil(e.next_due); return d === null || d > 90 }) },
  ]

  return (
    <div className="page-body">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Status Board</h1>
          <p className="page-subtitle">Item status and upcoming payments across your property</p>
        </div>
        {homes.length > 1 && (
          <select className="form-select" style={{ width: 'auto', minWidth: 180 }} value={selectedHome || ''} onChange={e => setSelectedHome(e.target.value)}>
            {homes.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
        )}
      </div>

      {/* Tabs */}
      <div className="page-tabs">
        <button className={`page-tab${activeTab === 'items' ? ' active' : ''}`} onClick={() => setActiveTab('items')}>
          Item Status
        </button>
        <button className={`page-tab${activeTab === 'payments' ? ' active' : ''}`} onClick={() => setActiveTab('payments')}>
          Upcoming Payments
          {overdueSoon.length > 0 && (
            <span className="badge" style={{ color: '#B45309', background: '#FFFBEB', marginLeft: 4 }}>
              {overdueSoon.length}
            </span>
          )}
        </button>
      </div>

      {dataLoading ? <Loading /> : (
        <>
          {/* ── Item Status tab ── */}
          {activeTab === 'items' && (
            <>
              {items.length > 0 && (
                <div className="stats-row">
                  <div className="stat-item"><span className="stat-value">{items.length}</span><span className="stat-label">Total Items</span></div>
                  <div className="stat-item"><span className="stat-value">{formatCurrency(committed)}</span><span className="stat-label">Committed</span></div>
                  <div className="stat-item"><span className="stat-value">{formatCurrency(totalEst)}</span><span className="stat-label">Total Est.</span></div>
                </div>
              )}
              {items.length === 0 ? (
                <EmptyState icon="📋" title="No items yet" description="Add items to rooms to see them here." />
              ) : (
                STATUSES.map(status => {
                  const group = items.filter(i => i.status === status)
                  if (!group.length) return null
                  const cfg = STATUS_CONFIG[status]
                  const grpTotal = group.reduce((s, i) => s + (i.cost || 0), 0)
                  return (
                    <div key={status} style={{ marginBottom: 32 }}>
                      <div className="section-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: cfg.color }} />
                          <span className="section-title">{status}</span>
                        </div>
                        <span style={{ fontSize: '.75rem', color: 'var(--ocean)' }}>
                          {group.length} item{group.length !== 1 ? 's' : ''}{grpTotal > 0 ? ` · ${formatCurrency(grpTotal)}` : ''}
                        </span>
                      </div>
                      <div className="data-table-wrap">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Item</th><th>Type</th><th>Space</th><th>Brand / Model</th><th>Cost</th><th>Links</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.map((item, idx) => (
                              <tr key={item.id} style={{ background: idx % 2 === 1 ? 'var(--offwhite)' : 'var(--white)' }}>
                                <td>
                                  <div style={{ fontWeight: 500 }}>{item.name}</div>
                                  {item.details && <div style={{ fontSize: '.75rem', color: 'var(--ocean)', marginTop: 2 }}>{item.details}</div>}
                                </td>
                                <td>{item.type && <TypeBadge type={item.type} />}</td>
                                <td>
                                  {item.rooms ? (
                                    <Link to={`/homes/${item.rooms.home_id}/rooms/${item.room_id}`} style={{ color: 'var(--navy)' }}>
                                      {item.rooms.name}
                                    </Link>
                                  ) : '—'}
                                </td>
                                <td>
                                  <div>{item.brand || '—'}</div>
                                  {item.model_number && <div style={{ fontSize: '.72rem', color: 'var(--ocean)', fontFamily: 'var(--font-mono)' }}>{item.model_number}</div>}
                                </td>
                                <td style={{ fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{item.cost ? formatCurrency(item.cost) : '—'}</td>
                                <td>
                                  <div style={{ display: 'flex', gap: 10 }}>
                                    {item.purchase_url && (
                                      <a href={item.purchase_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '.8rem', color: 'var(--navy)' }}>
                                        <ExternalLink size={11} /> Buy
                                      </a>
                                    )}
                                    {item.warranty_url && (
                                      <a href={item.warranty_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '.8rem', color: 'var(--ocean)' }}>
                                        <FileText size={11} /> Warranty
                                      </a>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
                })
              )}
            </>
          )}

          {/* ── Upcoming Payments tab ── */}
          {activeTab === 'payments' && (
            <>
              {expenses.length > 0 && (
                <div className="stats-row">
                  <div className="stat-item"><span className="stat-value">{expenses.length}</span><span className="stat-label">Recurring Expenses</span></div>
                  <div className="stat-item"><span className="stat-value">{formatCurrency(totalAnnualExp)}</span><span className="stat-label">Annual Total</span></div>
                  {expGroups[0].items.length > 0 && (
                    <div className="stat-item">
                      <span className="stat-value" style={{ color: '#B91C1C' }}>{expGroups[0].items.length}</span>
                      <span className="stat-label">Overdue</span>
                    </div>
                  )}
                  <div className="stat-item">
                    <span className="stat-value" style={{ color: expGroups[1].items.length > 0 ? '#B45309' : undefined }}>{expGroups[1].items.length}</span>
                    <span className="stat-label">Due Soon</span>
                  </div>
                </div>
              )}
              {expenses.length === 0 ? (
                <EmptyState icon="📅" title="No recurring expenses" description="Add expenses to a property to track upcoming payments here." />
              ) : (
                expGroups.map(group => {
                  if (!group.items.length) return null
                  const groupTotal = group.items.reduce((s, e) => s + (e.cost || 0), 0)
                  return (
                    <div key={group.label} style={{ marginBottom: 32 }}>
                      <div className="section-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: group.dot }} />
                          <span className="section-title">{group.label}</span>
                        </div>
                        <span style={{ fontSize: '.75rem', color: 'var(--ocean)' }}>
                          {group.items.length} payment{group.items.length !== 1 ? 's' : ''} · {formatCurrency(groupTotal)} due
                        </span>
                      </div>
                      <div className="data-table-wrap">
                        {group.items.map((exp, idx) => {
                          const days = daysUntil(exp.next_due)
                          const annualAmt = (exp.cost || 0) * (ANNUAL_MULTIPLIER[exp.period] || 1)
                          return (
                            <div key={exp.id} className="payment-row" style={{ background: idx % 2 === 1 ? 'var(--offwhite)' : 'var(--white)' }}>
                              <div>
                                <div style={{ fontWeight: 500, fontSize: '.875rem', marginBottom: 2 }}>{exp.name}</div>
                                <div style={{ fontSize: '.75rem', color: 'var(--ocean)' }}>{exp.provider || exp.type} · {exp.period}</div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 500, fontSize: '.9375rem' }}>{formatCurrency(exp.cost)}</div>
                                <div style={{ fontSize: '.7rem', color: 'var(--ocean)' }}>{formatCurrency(annualAmt)}/yr</div>
                              </div>
                              <UrgencyBadge days={days} dateStr={exp.next_due} />
                              <div>
                                {exp.doc_url && (
                                  <a href={exp.doc_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 3, fontSize: '.8rem' }}>
                                    <FileText size={12} /> Doc
                                  </a>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
